import React, { useEffect, useState } from 'react';
import { useDragModal } from '../../../customHooks/useDragModal';
import Arrow from './Arrow/Arrow';
import { ProductCard } from './ProductCard';
import CloseIcon from '../../../icons/X.svg';
import GenericClock from '../../../icons/GenericClock';
import * as translator from '../../../utils/translator';
import './AlternativeOfferModal.css';

export type PreOrderAlternativeOffer = {
  originalProduct: {
    product_item_id: number;
    price: number;
    titleView: string;
    imageSrc: string;
    catalogPage: string;
    cannabisCatalogProductProperties: {
      thc: number | null;
      cbd: number | null;
      type: string | null;
      genetic: string | null;
      strain: string | null;
    };
  } | null;
  alternative: {
    productItemID: number;
    price: number;
    thc: number | null;
    cbd: number | null;
    strain: string | null;
    titleView: string;
    imageSrc: string;
    catalogPage: string;
  } | null;
};

interface AlternativeOfferModalChooseTreatmentProps {
  alternatives: Record<string, PreOrderAlternativeOffer>;
  onAccept: (position: string) => void;
  onCancel: (position: string) => void;
  onWait: (position: string) => void;
  closeModal: () => void;
  language: string;
}

const DECISION_WAIT_TIMEOUT_MS = 60000;

const AlternativeOfferModalChooseTreatment: React.FC<AlternativeOfferModalChooseTreatmentProps> = ({
  alternatives,
  onAccept,
  onCancel,
  onWait,
  closeModal,
  language
}) => {
  // Get ordered list of availableAlternatives with valid alternatives
  const availableAlternatives = Object.entries(alternatives)
    .filter(([_, offer]) => offer.originalProduct !== null && offer.alternative !== null)
    .map(([position]) => position)
    .sort();

  const [currentQueueIndex, setCurrentQueueIndex] = useState(0);
  const hasMultipleOffers = availableAlternatives.length > 1;

  const shouldShowModal = availableAlternatives.length > 0;
  const currentPosition = availableAlternatives[currentQueueIndex];

  // If this offer sits on screen for 1 minute with no Accept/Cancel, track Decision = 3 (Wait).
  // Cleanup runs on every position change and on unmount (modal closed via X/outside-click/last-decision),
  // so the timer can only ever fire while this exact offer is still the one visible.
  useEffect(() => {
    if (!currentPosition) return;
    const timer = setTimeout(() => onWait(currentPosition), DECISION_WAIT_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, [currentPosition]);

  const {
    handleTouchStart,
    handleTouchMove,
    handleTouchEnd,
    currentTranslateY,
    modalDragTransition,
    isOpening,
    isClosing,
    closeModal: closeModalWithAnimation
  } = useDragModal({
    onClose: closeModal,
    shouldShow: shouldShowModal,
    preventBodyVerticalScroll: true
  });

  // Advance to the next out-of-stock product in the queue, or close once all have been decided
  const advanceQueue = () => {
    if (currentQueueIndex < availableAlternatives.length - 1) {
      setCurrentQueueIndex(currentQueueIndex + 1);
    } else {
      closeModalWithAnimation();
    }
  };

  const handleAccept = () => {
    onAccept(availableAlternatives[currentQueueIndex]);
    advanceQueue();
  };

  const handleCancel = () => {
    onCancel(availableAlternatives[currentQueueIndex]);
    advanceQueue();
  };

  if (!shouldShowModal) return null;

  const { originalProduct, alternative } = alternatives[currentPosition];

  if (!originalProduct || !alternative) return null;

  return (
    <div
      className={`alternative-offer-modal-background ${shouldShowModal && !isClosing ? (isOpening ? 'opening' : 'visible') : ''} ${isClosing ? 'closing' : ''}`}
      onClick={closeModalWithAnimation}
    >
      <div
        id="alternative-offer-modal-container"
        className={`alternative-offer-modal-container ${isClosing ? 'closing' : ''}`}
        onClick={(e) => e.stopPropagation()}
        style={{
          transform: `translateY(${currentTranslateY}px)`,
          transition: modalDragTransition
        }}
      >
        {/* Header */}
        <div className="alternative-offer-modal-header">
          <button className="out-of-stock-close-button" onClick={closeModalWithAnimation}>
            <img src={CloseIcon} alt="Close" />
          </button>

          <div
            className="open-close-drag-bar"
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
          />

          <div className="top-attention-sign desktop-only">
            {translator.translate(language, 'choose_treatment', 'alternative_offer/urgent_order_update') || 'DRINGEND: BESTELL-UPDATE'}
          </div>
        </div>

        {/* Body */}
        <div className="alternative-offer-modal-main-content">
          <div className="top-attention-sign mobile-only">
            {translator.translate(language, 'choose_treatment', 'alternative_offer/urgent_order_update') || 'DRINGEND: BESTELL-UPDATE'}
          </div>

          <h1 className="alternative-offer-modal-main-title">
            {translator.translate(language, 'choose_treatment', 'alternative_offer/main_title_single_product_default') || 'Die Apotheke hat uns gerade darüber informiert, dass das von Ihnen bestellte Sorte nicht mehr vorrätig ist.'}
          </h1>

          {/* Limited Availability Box */}
          <div className="out-of-stock-limited-availablity-box">
            <div className="limited-availablity-header">
              <div className="limited-availablity-icon">
                <GenericClock color="#0A9281" size={20} />
              </div>
              <p className="limited-availablity-title">
                {translator.translate(language, 'choose_treatment', 'alternative_offer/limited_availability_title') || 'Begrenzte Verfügbarkeit!'}
              </p>
            </div>
            <p className="limited-availablity-description">
              {translator.translate(language, 'choose_treatment', 'alternative_offer/limited_availability_description') || 'Unser Lagerbestand ändert sich schnell und dieses alternative Arzneimittel wurde noch nicht für Sie reserviert. Handeln Sie jetzt, um sich Ihren Vorrat zu sichern, bevor er vergriffen ist.'}
            </p>
          </div>

          {/* Instructions */}
          <p className="out-of-stock-offer-description">
            {translator.translate(language, 'choose_treatment', 'alternative_offer/same_genetic_alternative') || 'Wir bieten Ihnen eine Alternative mit gleicher Genetik an. Bitte bestätigen.'}
          </p>

          {/* Counter */}
          {hasMultipleOffers && (
            <p className="out-of-stock-offer-counter text-base">
              {currentQueueIndex + 1} / {availableAlternatives.length} {translator.translate(language, 'choose_treatment', 'alternative_offer/not_available') || 'nicht verfügbar'}
            </p>
          )}   

          {/* Alternative container - Side by side cards */}
          <div className="alternative-product-offer-container">
            <p className="alternative-for">
              {translator.translate(language, 'choose_treatment', 'alternative_offer/alternative_for') || 'Alternative für'}
              {' '}
              <span className="alternative-for-original-product-name">{originalProduct.titleView}</span>
            </p>

            <div className="alternative-product-transition-container" key={currentPosition}>
              {/* Original product */}
              <ProductCard
                titleView={originalProduct.titleView}
                imageSrc={originalProduct.imageSrc}
                thc={originalProduct.cannabisCatalogProductProperties.thc}
                cbd={originalProduct.cannabisCatalogProductProperties.cbd}
                strain={originalProduct.cannabisCatalogProductProperties.strain}
                price={originalProduct.price}
                catalogPage={originalProduct.catalogPage}
                isOutOfStock={true}
              />

              {/* Arrow */}
              <Arrow thickness={1} size={30} />

              {/* Alternative product */}
              <ProductCard
                titleView={alternative.titleView}
                imageSrc={alternative.imageSrc}
                thc={alternative.thc}
                cbd={alternative.cbd}
                strain={alternative.strain}
                price={alternative.price}
                catalogPage={alternative.catalogPage}
              />
            </div>
            {/* Actions */}
            <div className="confirmation-section gap-4 rounded-md ">
              <button className="accept-offer-button action-button" id="accept-alternative-button" onClick={handleAccept}>
                {translator.translate(language, 'choose_treatment', 'alternative_offer/accept_alternative')}
              </button>
              <button className="reject-offer-button" id="reject-offer-button text-[#747474]" onClick={handleCancel}>
                {translator.translate(language, 'choose_treatment', 'alternative_offer/cancel')}
              </button>
            </div>
          </div>

          {/* Disclaimer */}
          {availableAlternatives.length > 0 && (
            <div className="out-of-stock-disclaimer">
              {translator.translate(language, 'choose_treatment', 'alternative_offer/disclaimer')}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AlternativeOfferModalChooseTreatment;
